import { useState } from 'react'
import { Building2, Search, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

const GRADE_TONE: Record<string, string> = {
  A: 'text-emerald bg-emerald/15 border-emerald/40',
  B: 'text-blue-light bg-blue/15 border-blue/40',
  C: 'text-amber-light bg-amber/15 border-amber/40',
  D: 'text-orange bg-orange/15 border-orange/40',
  F: 'text-rose bg-rose/15 border-rose/40',
}

export default function VendorRiskPage() {
  const [domain, setDomain] = useState('')
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const score = async () => {
    if (domain.trim().length < 4) return
    setLoading(true); setData(null)
    try {
      const r = await api.get('/api/cyber/vendor-risk', { params: { domain: domain.trim() }, timeout: 90_000 })
      setData(r.data)
    } catch {} finally { setLoading(false) }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Building2 size={24} className="text-cyan" /> Vendor Risk Scorecard
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          BitSight-lite: passive recon score from DNS posture + breach history + attack surface + Shodan reported CVEs.
        </p>
      </div>

      <div className="card p-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input value={domain} onChange={e => setDomain(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && score()}
              placeholder="vendor domain (e.g. snowflake.com)"
              className="input pl-9 py-2 text-sm w-full" />
          </div>
          <button type="button" onClick={score} disabled={loading} className="btn-primary text-sm">
            {loading ? <RefreshCw size={12} className="animate-spin" /> : null}
            {loading ? 'Scoring…' : 'Score'}
          </button>
        </div>
        <p className="text-[10px] text-text-muted mt-2">
          Takes 30-60s on first hit (DNS + HIBP + crt.sh + Shodan InternetDB queries). Cached 1h.
        </p>
      </div>

      {data && (
        <>
          <div className="card p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-text-muted">{data.vendor}</div>
              <div className="flex items-baseline gap-3 mt-1">
                <span className="text-4xl font-bold font-mono text-text-primary">{data.overall}/100</span>
                <span className={clsx('px-3 py-1 rounded-md border text-2xl font-mono font-bold', GRADE_TONE[data.grade])}>
                  {data.grade}
                </span>
              </div>
              {data.ip && <div className="text-[10px] text-text-muted mt-1 font-mono">resolves to {data.ip}</div>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(data.components || {}).map(([key, comp]: any) => (
              <div key={key} className="card p-3">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-xs font-semibold text-text-primary uppercase tracking-wider">{key}</span>
                  <span className="text-2xl font-mono font-bold text-text-primary">{comp.score}</span>
                </div>
                {key === 'breach' && comp.count != null && (
                  <div className="text-[11px] text-text-muted">{comp.count} HIBP breach{comp.count === 1 ? '' : 'es'}</div>
                )}
                {key === 'surface' && comp.subdomain_count != null && (
                  <div className="text-[11px] text-text-muted">{comp.subdomain_count} subdomain{comp.subdomain_count === 1 ? '' : 's'} in CT logs</div>
                )}
                {key === 'shodan' && (
                  <div className="text-[11px] text-text-muted space-y-1 mt-1">
                    {comp.risky_ports?.length > 0 && (
                      <div className="flex items-center gap-1 text-rose-light">
                        <AlertTriangle size={10} /> Risky open ports: <span className="font-mono">{comp.risky_ports.join(', ')}</span>
                      </div>
                    )}
                    {comp.reported_vulns?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {comp.reported_vulns.map((v: string) => (
                          <span key={v} className="font-mono text-[9px] px-1 py-0.5 rounded bg-rose/10 text-rose-light">{v}</span>
                        ))}
                      </div>
                    )}
                    {(!comp.risky_ports?.length && !comp.reported_vulns?.length) && (
                      <div className="flex items-center gap-1 text-emerald"><CheckCircle2 size={10} /> No risky ports / known CVEs</div>
                    )}
                  </div>
                )}
                {key === 'posture' && comp.grade && (
                  <div className="text-[11px] text-text-muted">DNS / TLS posture grade: {comp.grade}</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
